import { SHEET_ID, COLS } from '../config.js';
import { state } from '../state.js';
import { $ } from '../utils/dom.js';
import { parseAmount } from '../utils/format.js';
import { parseGvizDate } from '../utils/date.js';
import { setLiveDot } from '../ui/header.js';
import { getActiveSheet, ALL_SHEETS } from '../sheetsStore.js';
import { fetchMetaSales } from './metaSales.js';
import { reconcileSales } from '../utils/reconcileSales.js';

/**
 * Carga datos de la(s) hoja(s) seleccionada(s) y los deja en `state.rows`.
 *
 * Tres modos según el valor de getActiveSheet():
 *   ''         → primera hoja del Sheet (gviz sin parámetro `sheet=`).
 *   '__ALL__'  → fetch en paralelo de TODAS las hojas detectadas en
 *                state.knownSheets, con SHEET_NAME en cada fila.
 *   <nombre>   → solo esa pestaña.
 */
export async function loadData(silent, afterLoadCb) {
  if (state.isLoading) return;
  state.isLoading = true;

  if (!silent) {
    $('status').textContent = 'Cargando datos…';
    $('status').classList.remove('err');
    $('status').style.display = '';
  }
  setLiveDot('loading');

  const active = getActiveSheet();

  try {
    if (active === ALL_SHEETS) {
      const sheets = state.knownSheets || [];
      if (!sheets.length) {
        throw new Error('Aún no se detectan las hojas. Espera unos segundos y vuelve a intentar.');
      }
      // Fetch en paralelo por GID (más confiable que por nombre — gviz cae
      // a la primera hoja silenciosamente cuando el nombre no resuelve).
      const results = await Promise.allSettled(
        sheets.map((s) => fetchSheetRows(s).then((rows) =>
          rows.map((r) => ({ ...r, SHEET_NAME: s.name }))
        ))
      );
      const allRows = [];
      const errors = [];
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') allRows.push(...r.value);
        else errors.push(`${sheets[i].name}: ${r.reason && r.reason.message || r.reason}`);
      });
      if (allRows.length === 0 && errors.length) {
        throw new Error('No se pudieron cargar las hojas: ' + errors.join('; '));
      }
      if (errors.length && !silent) {
        console.warn('Algunas hojas fallaron:', errors);
      }
      state.rows = allRows;
    } else if (active === '') {
      // "Hoja por defecto": gviz sin parámetros — devuelve la primera hoja
      // con datos.
      state.rows = await fetchSheetRows(null);
    } else {
      // Hoja específica: resolver name → gid usando state.knownSheets.
      // Si la lista todavía no se descubrió, fallback a &sheet=NAME (poco
      // confiable pero mejor que nada).
      const found = (state.knownSheets || []).find((s) => s.name === active);
      state.rows = await fetchSheetRows(found || { name: active });
    }

    try {
      const meta = await fetchMetaSales();
      state.metaBackendConfigured = meta.configured === true;
      state.metaSummary = meta.summary || state.metaSummary;
      const reconciled = reconcileSales(state.rows, Array.isArray(meta.sales) ? meta.sales : []);
      state.rows = reconciled.rows;
      state.reconciliationReport = reconciled.report;
    } catch {
      state.metaBackendConfigured = false;
      state.metaSummary = { sent: 0, pending: 0, failed: 0, blocked: 0, missingCtwaClid: 0, test: 0 };
      state.reconciliationReport = {
        reconciled: 0,
        webhookOnly: 0,
        unmatchedSheets: state.rows.length,
        ambiguous: 0,
      };
    }

    if (afterLoadCb) afterLoadCb(silent);
  } catch (e) {
    setLiveDot('error');
    if (!silent) {
      $('status').textContent = 'Error al procesar datos: ' + e.message;
      $('status').classList.add('err');
      $('status').style.display = '';
    }
    console.error(e);
  } finally {
    state.isLoading = false;
  }
}

/**
 * Trae las filas de TODAS las hojas de venta en paralelo y las devuelve
 * combinadas, cada una etiquetada con SHEET_NAME. NO toca state.rows ni
 * dispara renders — la usa la vista "Datos Generales" para tener su propio
 * dataset agregado independiente del dropdown del tab Ventas.
 *
 * @returns {Promise<object[]>}
 */
export async function fetchAllSalesRows() {
  const sheets = state.knownSheets || [];
  if (!sheets.length) return [];
  const results = await Promise.allSettled(
    sheets.map((s) => fetchSheetRows(s).then((rows) =>
      rows.map((r) => ({ ...r, SHEET_NAME: s.name }))
    ))
  );
  const out = [];
  results.forEach((r) => {
    if (r.status === 'fulfilled') out.push(...r.value);
  });
  return out;
}

/**
 * Trae las filas de UNA hoja vía gviz/tq (JSONP) y devuelve un array parseado.
 * No toca state.rows ni dispara renders — es responsabilidad de loadData().
 *
 * @param {{name?: string, gid?: string} | null} sheet
 *        - null: hoja por defecto (sin parámetros)
 *        - {gid}: direccionar por gid (preferido — confiable)
 *        - {name}: fallback por nombre (gviz cae al default si no resuelve)
 * @returns {Promise<object[]>}
 */
function fetchSheetRows(sheet) {
  return new Promise((resolve, reject) => {
    const cbName = '__sheetCb_' + Date.now() + '_' + Math.floor(Math.random() * 1e6);
    let sheetParam = '';
    let labelForError = 'hoja por defecto';
    if (sheet) {
      if (sheet.gid != null) {
        sheetParam = `&gid=${encodeURIComponent(sheet.gid)}`;
        labelForError = sheet.name || `gid=${sheet.gid}`;
      } else if (sheet.name) {
        sheetParam = `&sheet=${encodeURIComponent(sheet.name)}`;
        labelForError = sheet.name;
      }
    }

    const cleanup = () => {
      try { delete window[cbName]; } catch (e) {}
      script.remove();
      clearTimeout(timer);
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(new Error('Timeout cargando ' + labelForError));
    }, 20000);

    window[cbName] = (data) => {
      cleanup();
      try {
        if (data && data.status === 'error') {
          const detail = (data.errors && data.errors[0] && (data.errors[0].detailed_message || data.errors[0].message)) || 'error desconocido';
          throw new Error(detail);
        }
        if (!data || !data.table) throw new Error('Respuesta inesperada de Google Sheets');

        const cols = data.table.cols.map((c) => (c.label || c.id || '').trim());
        const idx = {};
        COLS.forEach((name) => {
          idx[name] = cols.findIndex((c) => c.toUpperCase() === name.toUpperCase());
        });

        const rows = (data.table.rows || []).map((row) => {
          const cells = row.c || [];
          const get = (name) => {
            const i = idx[name];
            if (i < 0 || !cells[i]) return '';
            const v = cells[i].v;
            const f = cells[i].f;
            if (v == null) return '';
            return f != null ? f : v;
          };
          const fechaCell = idx['FECHA'] >= 0 ? cells[idx['FECHA']] : null;
          const fechaVal = fechaCell ? fechaCell.v : null;
          const date = parseGvizDate(fechaVal);
          const fechaRaw = fechaCell ? (fechaCell.f || String(fechaCell.v || '')) : '';
          const pagoVal = idx['PAGO RECIBIDO'] >= 0 && cells[idx['PAGO RECIBIDO']] ? cells[idx['PAGO RECIBIDO']] : null;
          const pagoStr = pagoVal ? (pagoVal.f || String(pagoVal.v || '')) : '';
          return {
            STATUS: String(get('STATUS') || '').trim(),
            NOMBRE: String(get('NOMBRE') || '').trim(),
            FECHA: date,
            FECHA_RAW: fechaRaw,
            NUMERO: String(get('NUMERO') || '').trim(),
            MONTO: typeof (pagoVal && pagoVal.v) === 'number' ? pagoVal.v : parseAmount(pagoStr),
            PAGO_RAW: pagoStr,
            BANCO: String(get('BANCO') || '').trim(),
            AD_ID: String(get('AD ID') || '').trim(),
            EMAIL: String(get('EMAIL') || '').trim(),
            POST_ID: String(get('POST ID') || '').trim(),
          };
        }).filter((r) => r.STATUS || r.NOMBRE || r.FECHA);

        resolve(rows);
      } catch (e) {
        reject(e);
      }
    };

    const script = document.createElement('script');
    script.src = `https://docs.google.com/spreadsheets/d/${SHEET_ID}/gviz/tq?tqx=out:json;responseHandler:${cbName}${sheetParam}&_=${Date.now()}`;
    script.onerror = () => {
      cleanup();
      reject(new Error(`No se pudo cargar ${labelForError}.`));
    };
    document.head.appendChild(script);
  });
}
